import React, { useState, useEffect } from "react";
import { getConcepts, createConcept, updateConcept, deleteConcept, launchConcept } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash, Rocket, Lightbulb, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUSES = ["brainstorming", "researching", "planning", "approved", "launched", "rejected"];

export default function Concepts() {
  const { hasPermission } = useAuth();
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [launchDialogOpen, setLaunchDialogOpen] = useState(false);
  const [editingConcept, setEditingConcept] = useState(null);
  const [deletingConcept, setDeletingConcept] = useState(null);
  const [launchingConcept, setLaunchingConcept] = useState(null);
  const [formData, setFormData] = useState({ name: "", niche: "", description: "", status: "brainstorming", target_launch_date: "" });

  const fetchConcepts = async () => {
    try {
      const response = await getConcepts();
      setConcepts(response.data);
    } catch (err) {
      toast.error("Failed to load concepts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConcepts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenCreate = () => {
    setEditingConcept(null);
    setFormData({ name: "", niche: "", description: "", status: "brainstorming", target_launch_date: "" });
    setDialogOpen(true);
  };

  const handleOpenEdit = (concept) => {
    setEditingConcept(concept);
    setFormData({ name: concept.name, niche: concept.niche, description: concept.description || "", status: concept.status, target_launch_date: concept.target_launch_date || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConcept) {
        await updateConcept(editingConcept.id, formData);
        toast.success("Concept updated");
      } else {
        await createConcept(formData);
        toast.success("Concept created");
      }
      setDialogOpen(false);
      fetchConcepts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save concept");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteConcept(deletingConcept.id);
      toast.success("Concept deleted");
      setDeleteDialogOpen(false);
      setDeletingConcept(null);
      fetchConcepts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete concept");
    }
  };

  const handleLaunch = async () => {
    try {
      await launchConcept(launchingConcept.id);
      toast.success("Concept launched as channel!");
      setLaunchDialogOpen(false);
      setLaunchingConcept(null);
      fetchConcepts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to launch concept");
    }
  };

  const canCreate = hasPermission("create_concepts");
  const canEdit = hasPermission("edit_concepts");
  const canDelete = hasPermission("delete_concepts");

  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="surface-card p-6"><div className="skeleton h-32 w-full" /></div>)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Channel Concepts</h1>
          <p className="text-gray-400 text-sm">Plan and develop new channel ideas</p>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="create-concept-btn">
            <Plus size={18} className="mr-2" />New Concept
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {concepts.map((concept) => (
          <div key={concept.id} className="surface-card p-6 hover:border-white/10 transition-all group" data-testid={`concept-card-${concept.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Lightbulb size={24} className="text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{concept.name}</h3>
                  <p className="text-sm text-gray-400">{concept.niche}</p>
                </div>
              </div>
              {(canEdit || canDelete) && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && concept.status !== "launched" && (
                    <button onClick={() => handleOpenEdit(concept)} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"><Pencil size={16} /></button>
                  )}
                  {canEdit && concept.status === "approved" && (
                    <button onClick={() => { setLaunchingConcept(concept); setLaunchDialogOpen(true); }} className="p-2 rounded-lg hover:bg-green-500/10 text-gray-400 hover:text-green-400"><Rocket size={16} /></button>
                  )}
                  {canDelete && concept.status !== "launched" && (
                    <button onClick={() => { setDeletingConcept(concept); setDeleteDialogOpen(true); }} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400"><Trash size={16} /></button>
                  )}
                </div>
              )}
            </div>

            {concept.description && <p className="text-sm text-gray-400 mb-4 line-clamp-2">{concept.description}</p>}

            <div className="flex items-center justify-between pt-4 border-t border-white/5">
              <span className={`badge ${
                concept.status === "launched" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                concept.status === "approved" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                concept.status === "rejected" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                "bg-gray-500/20 text-gray-400 border-gray-500/30"
              }`}>{concept.status}</span>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {concept.target_launch_date && (
                  <div className="flex items-center gap-1"><CalendarBlank size={12} />{concept.target_launch_date}</div>
                )}
                <span>{concept.idea_count || 0} ideas</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {concepts.length === 0 && (
        <div className="text-center py-16">
          <Lightbulb size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No concepts yet</h3>
          <p className="text-gray-400 mb-4">Start planning your next channel</p>
          {canCreate && <Button onClick={handleOpenCreate} className="btn-primary"><Plus size={18} className="mr-2" />Create Concept</Button>}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editingConcept ? "Edit Concept" : "New Concept"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300">Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Channel concept name" required data-testid="concept-name-input" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Niche</Label><Input value={formData.niche} onChange={(e) => setFormData({ ...formData, niche: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Food, Tech, etc." required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="text-gray-300">Target Launch</Label><Input type="date" value={formData.target_launch_date} onChange={(e) => setFormData({ ...formData, target_launch_date: e.target.value })} className="bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div className="space-y-2"><Label className="text-gray-300">Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white/5 border-white/10 text-white min-h-[80px]" placeholder="What's this channel about?" /></div>
            <div className="flex gap-3 pt-4"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button><Button type="submit" className="flex-1 btn-primary" data-testid="concept-submit-btn">{editingConcept ? "Save" : "Create"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Concept?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">This will permanently delete "{deletingConcept?.name}" and all its ideas.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={launchDialogOpen} onOpenChange={setLaunchDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Launch Concept?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">This will create a new channel "{launchingConcept?.name}" and convert all ideas to videos.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleLaunch} className="bg-green-600 hover:bg-green-500 text-white">Launch</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
